<script setup>
import { computed, ref, watch } from "vue";

import { onboardingPiCreated, onboardingStepCompleted } from "@/analytics/events";
import { trackEvent } from "@/analytics/track";
import Dialog from "@/components-ng/ui/dialog/Dialog.vue";
import DialogContent from "@/components-ng/ui/dialog/DialogContent.vue";
import DialogDescription from "@/components-ng/ui/dialog/DialogDescription.vue";
import DialogTitle from "@/components-ng/ui/dialog/DialogTitle.vue";
import { OnboardingHandler } from "@/handlers/onboardingHandler";
import { createHandlerContext } from "@/stores/context";
import { useToastStore } from "@/stores/toast";

import BaseButton from "../BaseButton.vue";
import BaseInput from "../BaseInput.vue";
import Chip from "../Chip.vue";
import SvgIcon from "../SvgIcon.vue";

const IP_LABEL = "IP";
const NONE_PRESET = "";
const MIN_TEAMS = 4;
const MIN_ITERATIONS = 3;
const MAX_ITERATIONS = 12;

const props = defineProps({
  open: { type: Boolean, default: false },
});
const emit = defineEmits(["update:open", "success"]);

const onboarding = new OnboardingHandler(createHandlerContext());
const toast = useToastStore();

const step = ref(1);
const artName = ref("");
const teams = ref(Array.from({ length: MIN_TEAMS }, (_, i) => `Team ${i + 1}`));
const sessionName = ref("");
const iterationCount = ref(5);
const newTeam = ref("");

const presets = ref([]);
const selectedPresetId = ref(NONE_PRESET);
const submitting = ref(false);
const submitError = ref("");

const presetChips = computed(() => [
  { label: "None", value: NONE_PRESET },
  ...presets.value.map((p) => ({ label: p.name, value: p.id })),
]);

const iterationNames = computed(() => {
  const list = [];
  for (let i = 1; i <= Number(iterationCount.value); i++) {
    list.push(`Iteration ${i}`);
  }
  list.push(IP_LABEL);
  return list;
});

watch(
  () => props.open,
  async (v) => {
    if (!v) {
      step.value = 1;
      artName.value = "";
      teams.value = Array.from({ length: MIN_TEAMS }, (_, i) => `Team ${i + 1}`);
      sessionName.value = "";
      iterationCount.value = 5;
      newTeam.value = "";
      submitError.value = "";
      return;
    }
    if (presets.value.length) return;
    try {
      presets.value = await onboarding.listPresets();
    } catch {
      presets.value = [];
      selectedPresetId.value = NONE_PRESET;
      toast.showMessage({
        message: "Couldn't load example stickies. You can still create a PI.",
        type: "error",
      });
      return;
    }
    const healthcare = presets.value.find((p) => p.name?.toLowerCase() === "healthcare");
    selectedPresetId.value = healthcare?.id ?? NONE_PRESET;
  },
);

const stepHeading = computed(() =>
  step.value === 1 ? "Your ART and teams" : "Your planning interval",
);
const stepSub = computed(() =>
  step.value === 1
    ? "Name your ART and list the teams on it."
    : "Name the PI, set the iterations, and start with a template.",
);

const cleanedTeams = computed(() => teams.value.map((t) => t.trim()).filter(Boolean));
const canAdvance = computed(() => !!artName.value.trim() && cleanedTeams.value.length >= MIN_TEAMS);
const canSubmit = computed(() => {
  const n = Number(iterationCount.value);
  return !!sessionName.value.trim() && n >= MIN_ITERATIONS && n <= MAX_ITERATIONS;
});

function addTeamChip() {
  const v = newTeam.value.trim();
  if (!v) return;
  teams.value.push(v);
  newTeam.value = "";
}
function removeTeam(i) {
  if (teams.value.length > MIN_TEAMS) teams.value.splice(i, 1);
}
function next() {
  if (canAdvance.value) {
    trackEvent(onboardingStepCompleted("art-teams"));
    step.value = 2;
  }
}
function back() {
  step.value = 1;
}
function close() {
  emit("update:open", false);
}
async function submit() {
  if (!canSubmit.value || submitting.value) return;
  submitting.value = true;
  submitError.value = "";
  try {
    const result = await onboarding.createSession({
      presetId: selectedPresetId.value || null,
      sessionName: sessionName.value.trim(),
      teamNames: cleanedTeams.value,
      iterationNames: iterationNames.value,
      artName: artName.value.trim(),
    });
    // Invite teammates onto the first team — the choice is arbitrary. Onboarding
    // always creates several teams; the success dialog just needs one to seed.
    const teamId = result?.team_ids?.[0];
    if (typeof teamId !== "number") {
      throw new Error("Onboarding response is missing team_ids.");
    }
    trackEvent(onboardingPiCreated(!!selectedPresetId.value));
    emit("success", {
      sessionId: result?.session_id ?? "",
      sessionName: sessionName.value.trim(),
      teamId,
    });
    emit("update:open", false);
  } catch (err) {
    submitError.value = err?.message || "Couldn't create your PI. Try again.";
  } finally {
    submitting.value = false;
  }
}

function onOpenChange(v) {
  emit("update:open", v);
}
</script>

<template>
  <Dialog :open @update:open="onOpenChange">
    <DialogContent class="onboard-dialog" @pointer-down-outside.prevent>
      <header class="head">
        <DialogTitle class="title">{{ stepHeading }}</DialogTitle>
        <DialogDescription class="sub">{{ stepSub }}</DialogDescription>
        <div class="dots" aria-label="Step indicator">
          <span class="dot" :class="{ active: step === 1 }" />
          <span class="dot" :class="{ active: step === 2 }" />
        </div>
      </header>

      <section v-if="step === 1" class="panel">
        <div class="row">
          <BaseInput v-model="artName" label="ART name" placeholder="e.g. Payments Platform" />
        </div>

        <div class="row">
          <span id="onb-teams-label" class="lbl">Teams</span>
          <div class="chips" role="group" aria-labelledby="onb-teams-label">
            <Chip v-for="(team, i) in teams" :key="i">
              <template #icon>
                <SvgIcon name="persona/team" width="14" height="14" />
              </template>
              <input
                v-model="teams[i]"
                class="chip-edit"
                :placeholder="`Team ${i + 1}`"
                :aria-label="`Team ${i + 1}`"
              />
              <template #trailing>
                <button
                  type="button"
                  class="chip-x"
                  :disabled="teams.length <= MIN_TEAMS"
                  :aria-label="`Remove ${team || `Team ${i + 1}`}`"
                  @click="removeTeam(i)"
                >
                  <SvgIcon name="system/close" width="10" height="10" />
                </button>
              </template>
            </Chip>
            <Chip dashed>
              <template #icon>
                <SvgIcon name="system/plus" width="12" height="12" />
              </template>
              <input
                v-model="newTeam"
                class="chip-add-input"
                placeholder="Add team"
                aria-label="Add team"
                @keydown.enter.prevent="addTeamChip"
                @keydown.tab="addTeamChip"
                @blur="addTeamChip"
              />
            </Chip>
          </div>
          <p class="lbl-sub">Minimum four teams to start a PI.</p>
        </div>
      </section>

      <section v-else class="panel">
        <div class="row two-col">
          <div>
            <label class="lbl" for="onb-pi">PI name</label>
            <BaseInput
              id="onb-pi"
              v-model="sessionName"
              placeholder="e.g. PI 2026.1 — Q1 Planning"
            />
          </div>
          <div>
            <label class="lbl" for="onb-it"> Iterations <span class="muted">(+ IP)</span> </label>
            <BaseInput
              id="onb-it"
              v-model="iterationCount"
              type="number"
              :min="MIN_ITERATIONS"
              :max="MAX_ITERATIONS"
            />
          </div>
        </div>

        <div class="row">
          <div class="lbl-row">
            <span id="onb-presets-label" class="lbl">Start with a template</span>
            <span class="recommended-badge">Recommended</span>
          </div>
          <p class="template-hint">
            Templates fill your boards with linked example stickies, so you can explore a real PI
            Planning board instead of a blank one. Pick None to start fresh.
          </p>
          <div class="pill-row" role="group" aria-labelledby="onb-presets-label">
            <button
              v-for="p in presetChips"
              :key="p.value"
              type="button"
              class="pill"
              :class="{ active: selectedPresetId === p.value }"
              @click="selectedPresetId = p.value"
            >
              {{ p.label }}
            </button>
          </div>
        </div>
      </section>

      <p v-if="submitError" class="submit-error">{{ submitError }}</p>

      <footer class="actions">
        <BaseButton v-if="step === 1" variant="ghost" color="grey" @click="close">
          Cancel
        </BaseButton>
        <BaseButton v-else variant="ghost" color="grey" @click="back"> ← Back </BaseButton>

        <BaseButton
          v-if="step === 1"
          variant="filled"
          color="primary"
          :disabled="!canAdvance"
          @click="next"
        >
          Continue →
        </BaseButton>
        <BaseButton
          v-else
          variant="filled"
          color="primary"
          :disabled="!canSubmit || submitting"
          @click="submit"
        >
          {{ submitting ? "Creating…" : "Create PI" }}
        </BaseButton>
      </footer>
    </DialogContent>
  </Dialog>
</template>

<style lang="scss" scoped>
@use "@/colors" as c;
@use "@/styles/mixins/typography";

// DialogContent is portal'd outside this component's DOM, so scoped selectors
// can't reach it. Style via the class we pass on the primitive.
:global(.onboard-dialog) {
  max-width: 680px;
  padding: 40px 44px 32px;
}

.head {
  margin-bottom: 36px;
  padding-right: 32px;
}

.title {
  @include typography.heading;

  color: c.$black;
  margin: 0 0 8px;
}

.sub {
  @include typography.medium;

  color: c.$text-subtle;
  margin: 0 0 16px;
}

.dots {
  display: flex;
  gap: 6px;
}

.dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: c.$grey-light;
  transition: background 0.15s;

  &.active {
    background: c.$black;
  }
}

.panel {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.row {
  display: flex;
  flex-direction: column;
}

.two-col {
  display: grid;
  grid-template-columns: 1fr 120px;
  gap: 12px;
}

.lbl {
  @include typography.small("semi-bold");

  color: c.$black;
  margin-bottom: 6px;

  .muted {
    @include typography.small("regular");

    color: c.$text-subtle;
  }
}

.lbl-sub {
  @include typography.extra-small;

  color: c.$text-subtle;
  margin: 8px 0 0;
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.chip-edit {
  @include typography.small("semi-bold");

  width: 90px;
  height: 100%;
  border: none;
  outline: none;
  background: transparent;
  color: c.$black;
}

.chip-x {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: none;
  background-color: c.$grey-lightest;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: c.$text-subtle;
  transition:
    background-color 0.15s,
    color 0.15s;

  &:hover:not(:disabled) {
    background-color: c.$grey-light;
    color: c.$black;
  }

  &:disabled {
    opacity: 0;
    cursor: not-allowed;
  }
}

.chip-add-input {
  @include typography.small;

  width: 120px;
  padding: 0;
  border: 0;
  outline: none;
  background: transparent;
  color: c.$black;

  &::placeholder {
    color: c.$text-subtle;
  }
}

.lbl-row {
  display: flex;
  align-items: center;
  gap: 8px;

  .lbl {
    margin-bottom: 0;
  }
}

.recommended-badge {
  @include typography.caption("semi-bold");

  color: c.$text-subtle;
  background-color: c.$grey-lightest;
  border-radius: 999px;
  padding: 2px 8px;
  text-transform: uppercase;
}

.template-hint {
  @include typography.extra-small;

  color: c.$text-subtle;
  margin: 8px 0 14px;
}

.pill-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.pill {
  appearance: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: c.$white;
  border: 1px solid c.$border;
  border-radius: 999px;
  padding: 0 12px;
  height: 30px;
  cursor: pointer;

  @include typography.small;

  color: c.$text-subtle;

  &:hover {
    border-color: c.$main;
    color: c.$black;
  }

  &.active {
    background-color: c.$main;
    border-color: c.$main;
    color: c.$main-inverse;
  }

  &.active:hover {
    background-color: c.$main-hover;
    border-color: c.$main-hover;
    color: c.$main-inverse;
  }
}

.submit-error {
  @include typography.small;

  color: c.$red;
  margin: 24px 0 0;
}

.actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 40px;
  padding-top: 24px;
  border-top: 1px solid c.$border-subtle;
}
</style>
